import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { type Control, Controller, type UseFormWatch } from 'react-hook-form'

import { getLegacySectionIds, getLegacySectionOption } from '@/constants/sections'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'

function toggleSection(
  checked: boolean,
  sectionId: string,
  currentFieldValue: string | string[] | undefined,
  onChange: (value: string[]) => void
) {
  const currentValue = Array.isArray(currentFieldValue) ? currentFieldValue : []
  if (checked) {
    onChange([...currentValue, sectionId])
    return
  }
  onChange(currentValue.filter((id) => id !== sectionId))
}

interface LegacySectionsSectionProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  watch: UseFormWatch<UserFormInput>
}

// Deprecated section permissions (pre-cs_* values such as `reports_all` or
// `*_vo` view-only roles) that may still linger on a user from the legacy
// admin/reports/op services. This card lists the ones a user currently holds so
// a sysop can remove them; it never appears for users with none. Values not yet
// in the enrichment catalog show by their raw key, so nothing is left stuck.
export function LegacySectionsSection({ control, watch }: LegacySectionsSectionProps) {
  // Snapshot the legacy ids the user loaded with, so a row stays visible after
  // it's unchecked and the removal can be undone before saving.
  const [legacyIds] = useState(() => {
    const initial = watch('attributes.sections')
    return getLegacySectionIds(Array.isArray(initial) ? initial : [])
  })

  if (legacyIds.length === 0) return null

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-6">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Legacy Sections Access
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Deprecated permissions carried over from the legacy services. Uncheck to remove them
            from this user.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {legacyIds.map((id) => (
            <LegacySectionCheckbox key={id} id={id} control={control} />
          ))}
        </div>

        <LegacyGuidelines />
      </div>
    </div>
  )
}

const LegacySectionCheckbox = ({
  id,
  control,
}: {
  id: string
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
}) => {
  const option = getLegacySectionOption(id)

  return (
    <Controller
      name="attributes.sections"
      control={control}
      render={({ field }) => {
        const current = Array.isArray(field.value) ? field.value : []
        const isChecked = current.includes(id)

        return (
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
            <input
              type="checkbox"
              id={`legacy-section-${id}`}
              checked={isChecked}
              onChange={(e) => {
                toggleSection(e.target.checked, id, field.value, field.onChange)
              }}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900"
            />
            <div className="flex-1 min-w-0">
              <label
                htmlFor={`legacy-section-${id}`}
                className="block text-sm font-medium cursor-pointer text-gray-900 dark:text-gray-100"
              >
                {option.name ?? id}
              </label>
              {option.name && (
                <p className="mt-0.5 text-xs font-mono text-gray-400 dark:text-gray-500">{id}</p>
              )}
              {option.description && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {option.description}
                </p>
              )}
            </div>
          </div>
        )
      }}
    />
  )
}

const LegacyGuidelines = () => (
  <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-800 dark:text-amber-200">
        <p className="font-medium mb-1">Deprecated permissions</p>
        <ul className="space-y-1 text-amber-700 dark:text-amber-300">
          <li>
            These sections come from the legacy services and are no longer granted by the portal
          </li>
          <li>Unchecking removes the permission when you save; leave it checked to keep it</li>
        </ul>
      </div>
    </div>
  </div>
)
